// Plán auditů (termíny, kalendář) a správa plánů.
// (Krok B1b: rozděleno ze src/app.js beze změny kódu; importy/exporty doplněny automaticky.)
import { auditGroupPickHtml, auditGroupOfType, ICONS, SCHEDULE_LATE_DEFAULT, auditGroupFilterFieldHtml, auditPresentParticipantIds, auditWorkplaceIds, branchLabel, cachedList, cachedValue, canManageContent, currentCompany, esc, fmtDateShort, fmtOccurrenceDate, isCompanyAccessible, matchesAuditGroup, normalizeSearchText, queueWrite, roleHasCompany, scopeBadgeHtml, scopeMine, scopeToggleHtml, filterActionsHtml, scopeWorkplaceSet, showToast, sortedAuditTypes, sortedCompanies, sortedWorkplaces, state, typesInGroup, workplacesLabel, workplaceLabel, wpFilterMatchesAny, wpFilterFieldsHtml } from '../core/state.js';
import { paginateList, paginationControlsHtml } from '../core/pagination.js';
import { db } from '../core/boot.js';
import { setScopeSilent } from '../core/companies.js';
import { confirmRow } from '../core/chrome.js';
import { settingsHeadHtml, settingsSectionHtml, settingsSwitchRowHtml } from '../core/ui-kit.js';
import { isFindingsTypeId, isScheduleOccurrenceStartableNow } from './audit-runner.js';
import { sortControlHtml } from './history.js';
import { MONTH_NAMES_CS } from '../core/pdf.js';
import { ensureRoleProfiles } from '../core/access.js';
import { dateTimeInputHtml, render } from '../main-render.js';
import { scheduleIncludesWorkplace, workplaceLabelOf, addScheduleInterval, addWorkdays, buildClosedAuditIndex, isOccurrenceFulfilled, isoDateOnly, isWeekendDay, lateDeadline, nextWorkday, parseISODateOnly, resolveCoAuditorIds, scheduleIntervalMs, scheduleOccurrencesInRange } from '../../supabase/functions/_shared/calc.js';

// ================= PLÁNOVÁNÍ AUDITŮ =================
// Appka sama nic nezakládá — plán je čistě výpočet "kdy má být další
// termín a kdo je na řadě", z data prvního termínu + frekvence. Žádný
// mutovatelný "ukazatel na další termín" se neukládá: kdykoli je potřeba
// termín pro libovolné období, spočítá se znovu (occurrence index k =
// 0,1,2,… => datum = start + k*frekvence, auditor = auditorIds[k %
// auditorIds.length]). "Splněno" se neukládá taky — odvozuje se z toho,
// jestli v okolí termínu existuje uzavřený audit pro stejné
// pracoviště+druh auditu.
var FREQ_UNIT_LABELS = {days:'dní', weeks:'týdnů', months:'měsíců', years:'let'};
function personDisplayName(id){
  var p = state.profiles.get(id);
  return (p && (p.displayName || p.email)) || 'Neznámá osoba';
}
// ---- Skupiny uživatelů (pro rotaci auditorů v plánech auditů) ----
// Jeden uživatel může být v libovolném počtu skupin. Skupina se v
// auditorIds rotace ukládá jako ODKAZ — řetězec "grp:<id>", nikdy jako
// zkopírovaný seznam členů — takže přidání/odebrání člena ze skupiny se
// promítne do všech plánů, které tu skupinu používají, hned při dalším
// zobrazení/generování termínů (nic se nikde nekopíruje, viz
// scheduleResolvedAuditorIds níž).
var AUDITOR_GROUP_PREFIX = 'grp:';
function isAuditorGroupEntry(entry){
  return typeof entry==='string' && entry.indexOf(AUDITOR_GROUP_PREFIX)===0;
}
function auditorGroupIdFromEntry(entry){
  return entry.slice(AUDITOR_GROUP_PREFIX.length);
}
function auditorGroupEntryFor(groupId){
  return AUDITOR_GROUP_PREFIX+groupId;
}
// Skupiny auditorů patří firmě (rozhodnutí uživatele 2026-11-02) — nabízí
// se jen skupiny aktivní firmy (plán i Nastavení pracují vždy s aktivní).
function sortedAuditorGroups(){
  var cid = state.currentCompanyId || '';
  return Array.from(state.auditorGroups.entries()).filter(function(e){ return (e[1].companyId||'')===cid; }).sort(function(a,b){
    return (a[1].name||'').localeCompare(b[1].name||'','cs');
  });
}
function auditorGroupDisplayName(groupId){
  var g = state.auditorGroups.get(groupId);
  return g ? (g.name||'(bez názvu)') : '(smazaná skupina)';
}
// Popisek jednoho záznamu v ULOŽENÉM pořadí rotace (osoba, nebo odkaz na
// skupinu) — pro zobrazení v editačním formuláři a v souhrnu plánu. Na
// rozdíl od scheduleResolvedAuditorIds skupinu NErozbaluje na členy.
function scheduleAuditorEntryLabel(entry){
  if(isAuditorGroupEntry(entry)) return 'Skupina: '+auditorGroupDisplayName(auditorGroupIdFromEntry(entry));
  return personDisplayName(entry);
}
// Skutečné pořadí rotace, se kterým se počítá: každý záznam skupiny se na
// svém místě "rozbalí" na aktuální členy, seřazené podle jména (viz
// odpověď uživatele — abecedně, ne podle pořadí přidání). Volá se znovu
// při každém výpočtu termínů, takže je vždy aktuální.
function scheduleResolvedAuditorIds(schedule){
  var raw = Array.isArray(schedule.auditorIds) ? schedule.auditorIds : [];
  var out = [];
  raw.forEach(function(entry){
    if(!entry) return;
    if(isAuditorGroupEntry(entry)){
      var g = state.auditorGroups.get(auditorGroupIdFromEntry(entry));
      var memberIds = (g && Array.isArray(g.memberIds)) ? g.memberIds.slice() : [];
      memberIds.sort(function(a,b){ return personDisplayName(a).localeCompare(personDisplayName(b),'cs'); });
      memberIds.forEach(function(uid){ out.push(uid); });
    } else {
      out.push(entry);
    }
  });
  return out;
}
// Spoluauditoři termínu: osoby a skupiny auditorů (grp:…) z coAuditorIds
// rozbalené na členy, bez auditora termínu a bez duplicit (rozhodnutí
// uživatele 2026-10-31). Stejně v Edge Functions (resolveCoAuditorIds).
function scheduleCoAuditorIdsFor(schedule, auditorId){
  return resolveCoAuditorIds(schedule, auditorId,
    function(gid){ var g = state.auditorGroups.get(gid); return (g && Array.isArray(g.memberIds)) ? g.memberIds : []; },
    personDisplayName);
}
// companyIds zde i všude níž: [] (prázdné) = "Všechny" — žádné omezení.
// Neřeší se jako samostatný příznak, protože to je přesně to, co appka
// jinak dělá s company-less (sdíleným) obsahem už dnes.
function usersWithCompanyAccess(companyIds){
  var ids = Array.isArray(companyIds) ? companyIds : [companyIds];
  return Array.from(state.roles.keys()).filter(function(id){
    var r = state.roles.get(id);
    if(!r) return false;
    return ids.some(function(cid){ return roleHasCompany(r, cid); });
  }).map(function(id){ return [id, personDisplayName(id)]; })
    .sort(function(a,b){ return a[1].localeCompare(b[1],'cs'); });
}
// Company-less pracoviště/druh auditu (companyId "" nebo chybí) jsou sdílené
// přes všechny subjekty — stejné pravidlo jako matchesCurrentCompany o pár
// řádků výš. Bez tohoto by "Druh auditu" v novém plánu zůstalo prázdné u
// každého subjektu, který dosud nemá svůj vlastní druh auditu (výchozí
// sada otázek je typicky company-less).
function branchesForCompany(companyId){
  return Array.from(state.branches.entries()).filter(function(e){ return !e[1].companyId || e[1].companyId===companyId; })
    .sort(function(a,b){ return (a[1].order||0)-(b[1].order||0); });
}
function workplacesForCompany(companyId){
  return Array.from(state.workplaces.entries()).filter(function(e){ return !e[1].companyId || e[1].companyId===companyId; })
    .sort(function(a,b){ return (a[1].name||'').localeCompare(b[1].name||'','cs'); });
}
function auditTypesForCompany(companyId){
  return Array.from(state.auditTypes.entries()).filter(function(e){ return !e[1].companyId || e[1].companyId===companyId; })
    .sort(function(a,b){ return (a[1].order||0)-(b[1].order||0); });
}
// ---- Rozsah plánu (subjekty/provozovny/pracoviště) — tři nezávislé
// "Všechny / jen vybrané" filtry, přesně jako skupina auditorů: prázdné
// pole = bez omezení, neprázdné = jen tyto konkrétní položky. Výsledný
// plán platí pro průnik všech tří. ----
function scheduleEffectiveCompanyIds(draftCompanyIds){
  var ids = draftCompanyIds && draftCompanyIds.length ? draftCompanyIds : sortedCompanies().map(function(e){ return e[0]; });
  return ids;
}
function scheduleBranchCandidates(effectiveCompanyIds){
  var seen = {}, out = [];
  effectiveCompanyIds.forEach(function(cid){
    branchesForCompany(cid).forEach(function(e){ if(!seen[e[0]]){ seen[e[0]]=true; out.push(e); } });
  });
  return out.sort(function(a,b){ return (a[1].order||0)-(b[1].order||0); });
}
function scheduleEffectiveBranchIds(draftBranchIds, effectiveCompanyIds){
  if(draftBranchIds && draftBranchIds.length) return draftBranchIds;
  var set = {};
  state.workplaces.forEach(function(w){
    if(effectiveCompanyIds.indexOf(w.companyId)!==-1) set[w.branchId||''] = true;
  });
  return Object.keys(set);
}
function scheduleWorkplaceCandidates(effectiveCompanyIds, effectiveBranchIds){
  return Array.from(state.workplaces.entries()).filter(function(e){
    return effectiveCompanyIds.indexOf(e[1].companyId)!==-1 && effectiveBranchIds.indexOf(e[1].branchId||'')!==-1;
  }).sort(function(a,b){ return (a[1].name||'').localeCompare(b[1].name||'','cs'); });
}
function scheduleAuditTypeCandidates(effectiveCompanyIds){
  var seen = {}, out = [];
  effectiveCompanyIds.forEach(function(cid){
    auditTypesForCompany(cid).forEach(function(e){ if(!seen[e[0]]){ seen[e[0]]=true; out.push(e); } });
  });
  return out.sort(function(a,b){ return (a[1].order||0)-(b[1].order||0); });
}
// Pracoviště, na která se plán skutečně vztahuje — průnik subjekt/provozovna/
// pracoviště filtrů, seřazeno deterministicky (subjekt → provozovna →
// pracoviště), protože pořadí v tomhle seznamu je přesně to "j", podle
// kterého se v jednom kole posouvá auditor dál (viz scheduleAuditorForIndex).
function scheduleMatchedWorkplaces(schedule){
  var byId = function(id){ return state.workplaces.get(id); };
  return Array.from(state.workplaces.entries()).filter(function(e){
    return scheduleIncludesWorkplace(schedule, e[0], e[1], function(id){ return state.workplaces.has(id); });
  }).sort(function(a,b){
    var ca = (state.companies.get(a[1].companyId)||{}).name||'';
    var cb = (state.companies.get(b[1].companyId)||{}).name||'';
    if(ca!==cb) return ca.localeCompare(cb,'cs');
    var ba = branchLabel(a[1].branchId), bb = branchLabel(b[1].branchId);
    if(ba!==bb) return ba.localeCompare(bb,'cs');
    return workplaceLabelOf(a[1], byId).localeCompare(workplaceLabelOf(b[1], byId),'cs');
  });
}
function scheduleScopeLabel(schedule, kind){
  var ids = schedule[kind];
  if(!Array.isArray(ids) || ids.length===0){
    return kind==='companyIds' ? 'Všechny subjekty' : kind==='branchIds' ? 'Všechny provozovny' : 'Všechna pracoviště';
  }
  if(kind==='companyIds') return ids.map(function(id){ return (state.companies.get(id)||{}).name||'?'; }).join(', ');
  if(kind==='branchIds') return ids.map(function(id){ return branchLabel(id); }).join(', ');
  return ids.map(function(id){ return workplaceLabel(id)||'?'; }).join(', ');
}
// Termíny plánu — sdílený výpočet (supabase/functions/_shared/calc.js, stejný v e-mailech a kalendáři).
function scheduleOccurrenceDates(schedule, rangeStart, rangeEnd){
  return scheduleOccurrencesInRange(schedule, rangeStart, rangeEnd);
}
function scheduleAuditorForIndex(schedule, index){
  var ids = scheduleResolvedAuditorIds(schedule).filter(Boolean);
  if(!ids.length) return null;
  return ids[index % ids.length];
}
// "Splněno" — dva způsoby párování audit → termín plánu, v tomto pořadí:
//
// 1) PŘÍMÁ VAZBA: audit založený přes "Zahájit audit" u konkrétního
//    termínu (App.startScheduledAudit) nese scheduleId + přesné datum
//    termínu (scheduleOccurrenceDate) — jednoznačně patří TÉTO jedné
//    occurrenci a žádné jiné, bez ohledu na časová okna. Tím pádem taky
//    jistota, že ho zahájil přiřazený auditor (to appka kontroluje už při
//    zahájení), takže se znovu nekontroluje.
//
// 2) ZÁLOŽNÍ PÁROVÁNÍ PODLE DATA: pro audity založené bez vazby na plán
//    (ruční "Nový audit") — stejné pracoviště+druh auditu, uzavřeno v
//    okně ± POLOVINA periody kolem termínu (ne celá perioda jako dřív —
//    u sousedních termínů by se okna celá perioda překrývala a jeden
//    audit by mylně splnil dva termíny najednou). Navíc musí sedět
//    přiřazený auditor termínu (pokud plán nějakého má) — jinak by audit
//    provedený kýmkoli jiným mylně oznacil termín za splněný.
//    Audity, které MAJÍ scheduleId (patří jinému konkrétnímu termínu,
//    třeba jinému datu stejného plánu), se do tohoto záložního párování
//    nezapočítávají — jejich osud je už rozhodnutý bodem 1) výš.
// Uzavřené (nesmazané) audity seskupené podle pracoviště a druhu auditu —
// dřív se pro každý termín procházely všechny audity (většina času
// každého překreslení kvůli číslům v navigaci). Během render() se rejstřík
// staví jednou, mimo render() pokaždé znovu (vždy aktuální).
function closedAuditsByWorkplaceType(){
  return cachedValue('closedAuditsByWpType', function(){ return buildClosedAuditIndex(state.audits.values()); });
}
// workplaceId = pracoviště termínu, nebo pole pracovišť (obchůzka přes víc
// pracovišť v jednom auditu, schedule.multiWorkplace) — audit bez vazby na
// termín ho splní, jen když pokrývá všechna. lateDays = lhůta po termínu
// (appSettings/scheduleLate): audit bez vazby uzavřený až po ní termín nesplní.
function isScheduleOccurrenceFulfilled(scheduleId, schedule, occDate, workplaceId, auditorId, lateDays){
  if(lateDays==null) lateDays = scheduleLateDays();
  return isOccurrenceFulfilled(closedAuditsByWorkplaceType(), scheduleId, schedule, occDate, workplaceId, auditorId, lateDays);
}
function scheduleLateDays(){
  return (typeof state.scheduleLateDays==='number') ? state.scheduleLateDays : SCHEDULE_LATE_DEFAULT;
}
// Doplní termínu lhůtu („Neprovedeno“) a zástup (schedule_delegations, klíč =
// klíč termínu; platí jen pro auditora, který termín předal).
function decorateOccurrence(o){
  o.expired = !o.fulfilled && Date.now() >= lateDeadline(o.date, scheduleLateDays());
  var d = state.delegations && state.delegations.get(o.key);
  o.delegateId = (d && d.delegateId && d.auditorId===o.auditorId && d.delegateId!==o.auditorId) ? d.delegateId : null;
  o.delegateName = o.delegateId ? personDisplayName(o.delegateId) : '';
  o.effectiveAuditorId = o.delegateId || o.auditorId;
  if(o.delegateId) o.coAuditorIds = (o.coAuditorIds||[]).filter(function(id){ return id!==o.delegateId; });
  return o;
}
function scheduleOccurrenceObjects(scheduleId, schedule, rangeStart, rangeEnd){
  var matched = scheduleMatchedWorkplaces(schedule);
  if(!matched.length) return [];
  var at = state.auditTypes.get(schedule.auditTypeId) || {};
  var now = Date.now();
  var out = [];
  function pushOccurrence(occ, wpId, wp, auditorId){
    var fulfilled = isScheduleOccurrenceFulfilled(scheduleId, schedule, occ.date, wpId, auditorId);
    out.push({
      key: scheduleId+'::'+occ.index+'::'+wpId,
      scheduleId: scheduleId,
      date: occ.date,
      dateStr: isoDateOnly(occ.date),
      time: schedule.time || '',
      companyId: wp.companyId,
      workplaceId: wpId, workplaceName: workplaceLabel(wp), branchName: branchLabel(wp.branchId),
      auditTypeId: schedule.auditTypeId, auditTypeName: at.name||'',
      scheduleName: schedule.name || at.name || '',
      auditorId: auditorId, auditorName: auditorId ? personDisplayName(auditorId) : '',
      coAuditorIds: scheduleCoAuditorIdsFor(schedule, auditorId),
      fulfilled: fulfilled,
      overdue: !fulfilled && occ.date.getTime() < now
    });
  }
  // Obchůzka: všechna pracoviště v jednom auditu (rozhodnutí uživatele
  // 2026-10-31) — jeden termín za kolo, auditor rotuje po kolech.
  if(schedule.multiWorkplace){
    var ids = matched.map(function(e){ return e[0]; });
    var branches = {};
    matched.forEach(function(e){ branches[e[1].branchId||''] = true; });
    var oneBranch = Object.keys(branches).length===1;
    scheduleOccurrenceDates(schedule, rangeStart, rangeEnd).forEach(function(occ){
      var auditorId = scheduleAuditorForIndex(schedule, occ.index);
      var fulfilled = isScheduleOccurrenceFulfilled(scheduleId, schedule, occ.date, ids, auditorId);
      out.push({
        key: scheduleId+'::'+occ.index+'::all',
        scheduleId: scheduleId,
        date: occ.date,
        dateStr: isoDateOnly(occ.date),
        time: schedule.time || '',
        companyId: matched[0][1].companyId,
        workplaceId: ids[0], workplaceIds: ids,
        workplaceName: workplacesLabel(matched.map(function(e){ return workplaceLabel(e[1]); })),
        branchName: oneBranch ? branchLabel(matched[0][1].branchId) : 'Více provozoven',
        auditTypeId: schedule.auditTypeId, auditTypeName: at.name||'',
        scheduleName: schedule.name || at.name || '',
        auditorId: auditorId, auditorName: auditorId ? personDisplayName(auditorId) : '',
        coAuditorIds: scheduleCoAuditorIdsFor(schedule, auditorId),
        fulfilled: fulfilled,
        overdue: !fulfilled && occ.date.getTime() < now
      });
    });
    return out.map(decorateOccurrence);
  }
  scheduleOccurrenceDates(schedule, rangeStart, rangeEnd).forEach(function(occ){
    if(schedule.spreadWorkplaces){
      var e = matched[occ.index % matched.length];
      pushOccurrence(occ, e[0], e[1], scheduleAuditorForIndex(schedule, occ.index));
    } else {
      matched.forEach(function(e, j){
        pushOccurrence(occ, e[0], e[1], scheduleAuditorForIndex(schedule, occ.index + j));
      });
    }
  });
  return out.map(decorateOccurrence);
}
function allVisibleScheduleOccurrences(rangeStart, rangeEnd){
  return cachedList('occ:'+(+rangeStart)+':'+(+rangeEnd)+':'+(state.currentCompanyId||''), function(){
    return computeVisibleScheduleOccurrences(rangeStart, rangeEnd);
  });
}
function computeVisibleScheduleOccurrences(rangeStart, rangeEnd){
  var out = [];
  state.schedules.forEach(function(s, id){
    if(!s || s.active===false) return;
    out = out.concat(scheduleOccurrenceObjects(id, s, rangeStart, rangeEnd));
  });
  // Viditelnost se řeší za jednotlivé termíny, ne za celý plán — jeden
  // plán "Všechny subjekty" může mít termíny v subjektech, které vidím,
  // i v těch, které nevidím.
  out = out.filter(function(o){
    if(!isCompanyAccessible(o.companyId)) return false;
    if(state.currentCompanyId && o.companyId && o.companyId!==state.currentCompanyId) return false;
    return true;
  });
  out.sort(function(a,b){ return a.date-b.date; });
  return out;
}
function scheduleListWindow(){
  var now = new Date();
  var start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  start.setDate(start.getDate()-120);
  var end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  end.setDate(end.getDate()+365);
  return {start:start, end:end};
}

function scheduleOccurrenceBadgeHtml(o){
  if(o.expired) return '<span class="badge badge-neutral">Neprovedeno</span>';
  if(o.overdue) return '<span class="badge badge-nok">Po termínu</span>';
  if(o.fulfilled) return '<span class="badge badge-ok">Proveden</span>';
  return '<span class="badge badge-na">Naplánováno</span>';
}
function scheduleOccurrenceStatus(o){
  if(o.expired) return 'neprovedeno';
  if(o.overdue) return 'po_terminu';
  if(o.fulfilled) return 'provedeno';
  return 'naplanovano';
}
function matchesScheduleSearch(o, term){
  if(!term) return true;
  var parts = [o.workplaceName, o.branchName, o.scheduleName, o.auditTypeName, o.auditorName];
  var hay = normalizeSearchText(parts.filter(Boolean).join(' '));
  return hay.indexOf(normalizeSearchText(term))>=0;
}
var SCHEDULE_SORT_OPTIONS = [
  {id:'termin', label:'Termín', defaultDir:'asc'},
  {id:'status', label:'Stav', defaultDir:'asc'},
  {id:'workplace', label:'Pracoviště', defaultDir:'asc'},
  {id:'auditType', label:'Druh', defaultDir:'asc'}
];
function scheduleSortValue(o, sortBy){
  switch(sortBy){
    case 'status':
      var order = {po_terminu:0, naplanovano:1, provedeno:2, neprovedeno:3};
      return order[scheduleOccurrenceStatus(o)];
    case 'workplace':
      return (o.workplaceName||'').toLowerCase();
    case 'auditType':
      return (o.auditTypeName||'').toLowerCase();
    case 'termin':
    default:
      return o.date.getTime();
  }
}
function filteredScheduleOccurrences(occList){
  var term = (state.scheduleSearch||'').trim().toLowerCase();
  return occList.filter(function(o){
    if(!wpFilterMatchesAny(state.scheduleFilterWp, o.workplaceIds || [o.workplaceId])) return false;
    if(state.scheduleFilterType && o.auditTypeId!==state.scheduleFilterType) return false;
    if(state.scheduleFilterGroup && !matchesAuditGroup(o.auditTypeId, state.scheduleFilterGroup)) return false;
    // „Naplánováno“ = všechny neprovedené v lhůtě, i po termínu (rozhodnutí uživatele 2026-10-31);
    // „Po termínu“ jen ty po termínu.
    if(state.scheduleFilterStatus){
      var st = scheduleOccurrenceStatus(o);
      if(!(st===state.scheduleFilterStatus || (state.scheduleFilterStatus==='naplanovano' && st==='po_terminu'))) return false;
    }
    if(!matchesScheduleSearch(o, term)) return false;
    return true;
  }).sort(function(a,b){
    var sortBy = state.scheduleSortBy || 'termin';
    var av = scheduleSortValue(a, sortBy);
    var bv = scheduleSortValue(b, sortBy);
    var cmp = (typeof av === 'string') ? av.localeCompare(bv, 'cs') : (av - bv);
    if(cmp===0) cmp = a.date.getTime() - b.date.getTime();
    return state.scheduleSortDir==='desc' ? -cmp : cmp;
  });
}
function dayWordCs(n){
  var abs = Math.abs(n);
  if(abs===1) return 'den';
  if(abs>=2 && abs<=4) return 'dny';
  return 'dní';
}
function scheduleRelativeDaysLabel(o){
  if(o.fulfilled) return '';
  var now = new Date();
  var today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  var occDate = new Date(o.date.getFullYear(), o.date.getMonth(), o.date.getDate());
  var diffDays = Math.round((occDate-today)/(1000*60*60*24));
  if(diffDays===0) return 'dnes';
  if(diffDays===1) return 'zítra';
  if(diffDays>1) return 'za '+diffDays+' '+dayWordCs(diffDays);
  var overdueDays = -diffDays;
  return 'po termínu o '+overdueDays+' '+dayWordCs(overdueDays);
}
function renderScheduleListHtml(occurrences, paginationHtml){
  if(occurrences.length===0){
    return '<div class="empty">'+ICONS.empty+'<p>V tomto období nejsou žádné naplánované termíny.</p></div>';
  }
  var html = '<div class="card">';
  occurrences.forEach(function(o,i){
    html += (i>0?'<div class="list-sep"></div>':'');
    var relLabel = scheduleRelativeDaysLabel(o);
    var canStart = !!(state.myId && o.effectiveAuditorId===state.myId && isScheduleOccurrenceStartableNow(o));
    html += '<div class="list-item" style="align-items:flex-start;padding:2px">' +
      '<div style="min-width:0;flex:1">' +
      '<div style="font-weight:600;font-size:var(--fs-md)">'+esc(o.workplaceName)+' · '+esc(o.branchName||'Sídlo firmy')+'</div>' +
      '<div class="muted" style="font-size:var(--fs-sm);margin-top:2px">'+esc(o.scheduleName||o.auditTypeName||'Audit')+'</div>' +
      '<div class="muted" style="font-size:var(--fs-sm);margin-top:2px">Termín <span style="'+(o.overdue&&!o.expired?'color:var(--nok);font-weight:700':'')+'">'+fmtOccurrenceDate(o)+'</span>'+
      (relLabel ? ' ('+relLabel+')' : '')+' · '+(o.auditorName?esc(o.auditorName):'bez auditora') +
      ((o.coAuditorIds||[]).length ? ' + '+esc((o.coAuditorIds||[]).map(personDisplayName).join(', ')) : '')+'</div>' +
      (o.delegateId ? '<div style="font-size:var(--fs-sm);margin-top:2px">Zástup: <b>'+esc(o.delegateName)+'</b></div>' : '') +
      '</div>' +
      '<div style="display:flex;flex-direction:column;align-items:flex-end;gap:6px;flex-shrink:0">' +
      scheduleOccurrenceBadgeHtml(o) +
      ((o.coAuditorIds||[]).indexOf(state.myId)>=0 ? '<span class="badge badge-neutral">Spoluauditor</span>' : '') +
      (o.delegateId && o.delegateId===state.myId ? '<span class="badge badge-neutral">Zástup za '+esc(o.auditorName)+'</span>' : '') +
      (canDelegate(o) ? '<button class="btn btn-sm btn-ghost" onclick="App.toggleDelegate(\''+o.key+'\')">'+(o.delegateId?'Změnit zástup':'Předat')+'</button>' : '') +
      (canStart ? '<button class="btn btn-sm btn-primary" onclick="App.startScheduledAudit(\''+o.key+'\')">Zahájit audit</button>' : '') +
      '</div>' +
      '</div>' + delegatePanelHtml(o);
  });
  html += '</div>';
  if(paginationHtml) html += paginationHtml;
  return html;
}
// ---------- zástup (rozhodnutí uživatele 2026-10-31) ----------
// Auditor může svůj termín (dokud není splněný a neuplynula lhůta) předat
// registrovanému uživateli s přístupem k firmě. Zástupce dostane e-mail hned.
function canDelegate(o){ return !!(state.myId && o.auditorId===state.myId && !o.fulfilled && !o.expired); }
function delegatePanelHtml(o){
  if(state.delegateOpenKey!==o.key || !canDelegate(o)) return '';
  var users = usersWithCompanyAccess(o.companyId).filter(function(e){ return e[0]!==state.myId; });
  return '<div class="delegate-panel">' +
    '<div class="field" style="margin-bottom:8px"><label>Zástupce</label><select id="delegateSel">' +
    '<option value="">— vyberte uživatele —</option>' +
    users.map(function(e){ return '<option value="'+e[0]+'" '+(o.delegateId===e[0]?'selected':'')+'>'+esc(e[1])+'</option>'; }).join('') +
    '</select></div>' +
    '<div class="set-note" style="margin:0 0 8px">Termín se nemění. Zástupce dostane e-mail a audit uvidí ve svém plánu.</div>' +
    '<div style="display:flex;gap:8px;flex-wrap:wrap">' +
    '<button class="btn btn-sm btn-primary" onclick="App.saveDelegate(\''+o.key+'\')">Předat</button>' +
    (o.delegateId ? '<button class="btn btn-sm btn-ghost btn-danger" onclick="App.cancelDelegate(\''+o.key+'\')">Zrušit předání</button>' : '') +
    '<button class="btn btn-sm btn-ghost" onclick="App.toggleDelegate(\''+o.key+'\')">Zavřít</button></div></div>';
}
function findOccurrence(key){
  var w = scheduleListWindow();
  return allVisibleScheduleOccurrences(w.start, w.end).filter(function(o){ return o.key===key; })[0] || null;
}
App.toggleDelegate = function(key){
  state.delegateOpenKey = state.delegateOpenKey===key ? null : key;
  render();
};
App.saveDelegate = function(key){
  var o = findOccurrence(key), sel = document.getElementById('delegateSel');
  if(!o || !canDelegate(o) || !db) return;
  var to = sel && sel.value;
  if(!to){ showToast('Vyberte zástupce.'); return; }
  var data = {scheduleId:o.scheduleId, dateStr:o.dateStr, companyId:o.companyId||'', auditorId:o.auditorId,
    delegateId:to, delegatedBy:state.myId, delegatedAt:new Date().toISOString()};
  state.delegateOpenKey = null;
  queueWrite(function(){ return db.doc('delegations/'+key).set(data); }).then(function(){
    showToast('Předáno: '+personDisplayName(to)+'. Posíláme e-mail.');
    if(window.__sbAuth && window.__sbAuth.notifyDelegation){
      window.__sbAuth.notifyDelegation(key).then(function(res){
        if(res && res.error) showToast('E-mail zástupci se nepodařilo odeslat: '+res.error.message);
      });
    }
  });
  render();
};
App.cancelDelegate = function(key){
  var o = findOccurrence(key);
  if(!o || o.auditorId!==state.myId || !db) return;
  state.delegateOpenKey = null;
  queueWrite(function(){ return db.doc('delegations/'+key).delete(); });
  showToast('Předání zrušeno.');
  render();
};
function isoWeekNumber(date){
  var d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  var dayNum = (d.getUTCDay()+6)%7; // 0 = pondělí
  d.setUTCDate(d.getUTCDate()-dayNum+3); // nejbližší čtvrtek téhož týdne
  var firstThursday = new Date(Date.UTC(d.getUTCFullYear(),0,4));
  var diff = d - firstThursday;
  return 1 + Math.round(diff/(7*24*3600*1000));
}
// Moje termíny = jsem auditor nebo spoluauditor plánu.
function isMyOccurrence(o){ return !!(state.myId && (o.auditorId===state.myId || o.delegateId===state.myId || (o.coAuditorIds||[]).indexOf(state.myId)>=0)); }
// Plán: „Moje“ = jsem auditor nebo spoluauditor, „Moje pracoviště“ = termíny na mých pracovištích.
function filterScheduleScope(list){
  if(scopeMine('schedule')) return list.filter(isMyOccurrence);
  var wpSet = scopeWorkplaceSet('schedule');
  return wpSet ? list.filter(function(o){ return (o.workplaceIds || [o.workplaceId]).some(function(w){ return !!wpSet[w]; }); }) : list;
}
function renderScheduleCalendarHtml(){
  var now = new Date();
  var y = state.scheduleCalendarY!=null ? state.scheduleCalendarY : now.getFullYear();
  var m = state.scheduleCalendarM!=null ? state.scheduleCalendarM : now.getMonth();
  var monthStart = new Date(y, m, 1);
  var monthEnd = new Date(y, m+1, 0, 23, 59, 59);
  var occ = allVisibleScheduleOccurrences(monthStart, monthEnd);
  occ = filterScheduleScope(occ);
  var byDay = {};
  occ.forEach(function(o){ (byDay[o.dateStr] = byDay[o.dateStr]||[]).push(o); });

  var monthNames = MONTH_NAMES_CS;
  var html = '<div class="sched-cal-head">' +
    '<button class="icon-btn" onclick="App.scheduleCalendarShift(-1)">'+ICONS.arrLeft+'</button>' +
    '<div style="font-weight:700;font-size:var(--fs-md)">'+monthNames[m]+' '+y+'</div>' +
    '<button class="icon-btn" onclick="App.scheduleCalendarShift(1)">'+ICONS.arrRight+'</button>' +
    '</div>';

  var firstWeekday = (monthStart.getDay()+6)%7; // 0 = pondělí
  var daysInMonth = monthEnd.getDate();
  var prevMonthDays = new Date(y, m, 0).getDate();
  var totalCells = firstWeekday + daysInMonth;
  var totalRows = Math.ceil(totalCells/7);
  var gridStart = new Date(y, m, 1-firstWeekday);

  html += '<div class="sched-cal-grid">';
  html += '<div class="sched-cal-wk-head" title="Číslo týdne">Týd.</div>';
  ['Po','Út','St','Čt','Pá','So','Ne'].forEach(function(d){ html += '<div class="sched-cal-dow">'+d+'</div>'; });

  var todayStr = isoDateOnly(now);
  for(var row=0; row<totalRows; row++){
    var rowDate = new Date(gridStart); rowDate.setDate(rowDate.getDate()+row*7);
    html += '<div class="sched-cal-wk">'+isoWeekNumber(rowDate)+'</div>';
    for(var col=0; col<7; col++){
      var idx = row*7+col;
      if(idx<firstWeekday){
        var dnum = prevMonthDays - firstWeekday + 1 + idx;
        html += '<div class="sched-cal-day sched-cal-outside"><div class="sched-cal-daynum">'+dnum+'</div></div>';
      } else if(idx<firstWeekday+daysInMonth){
        var day = idx - firstWeekday + 1;
        var dateStr = y+'-'+String(m+1).padStart(2,'0')+'-'+String(day).padStart(2,'0');
        var list = byDay[dateStr] || [];
        var cls = 'sched-cal-day';
        if(dateStr===todayStr) cls += ' sched-cal-today';
        if(state.scheduleCalendarSelectedDay===dateStr) cls += ' sched-cal-selected';
        var itemsHtml = '';
        if(list.length){
          var shown = list.slice(0,2);
          itemsHtml = '<div class="sched-cal-items">' + shown.map(function(o){
            var statusCls = o.expired?'neutral':(o.overdue?'nok':(o.fulfilled?'ok':'na'));
            return '<div class="sched-cal-item '+statusCls+'">'+esc(o.workplaceName||'')+'</div>';
          }).join('') + (function(){
            var rest = list.length-shown.length;
            if(rest<=0) return '';
            var word = (rest>=2&&rest<=4) ? 'další' : 'dalších';
            return '<div class="sched-cal-item-more">+'+rest+' '+word+'</div>';
          })() + '</div>';
        }
        html += '<div class="'+cls+'" onclick="App.selectScheduleCalendarDay(\''+dateStr+'\')">' +
          '<div class="sched-cal-daynum">'+day+'</div>' +
          (list.length ? '<div class="sched-cal-dots">'+list.map(function(o){
            return '<span class="sched-cal-dot '+(o.expired?'':(o.overdue?'nok':(o.fulfilled?'ok':'')))+'"></span>';
          }).join('')+'</div>' : '') +
          itemsHtml +
          '</div>';
      } else {
        var t = idx - (firstWeekday+daysInMonth) + 1;
        html += '<div class="sched-cal-day sched-cal-outside"><div class="sched-cal-daynum">'+t+'</div></div>';
      }
    }
  }
  html += '</div>';

  var selected = state.scheduleCalendarSelectedDay;
  if(selected && byDay[selected] && byDay[selected].length){
    html += '<h3 class="set-section" style="margin-top:16px">'+fmtDateShort(selected)+'</h3>';
    html += renderScheduleListHtml(byDay[selected]);
  } else if(selected){
    html += '<p class="muted" style="font-size:var(--fs-base);margin-top:14px">V tento den nejsou naplánované žádné audity.</p>';
  }
  return html;
}
function renderSchedule(){
  var html = '<div class="view-head" style="display:flex;align-items:flex-start;justify-content:space-between;gap:10px">' +
    '<div><h2>Plán auditů</h2><p>'+esc(('Nadcházející a po termínu plánované audity'+
    (currentCompany() ? ' pro '+currentCompany().name : '')).replace(/\.?$/, '.'))+'</p></div>' +
    (canManageContent() ? '<button type="button" class="btn btn-sm btn-primary" style="flex-shrink:0;margin-top:4px" onclick="App.setView(\'scheduleManage\')">Správa plánů</button>' : '') +
    '</div>';

  if(state.schedules.size===0){
    html += '<div class="empty">'+ICONS.empty+'<p>Zatím není nastaven žádný plán auditů.'+(canManageContent()?' Založte ho přes tlačítko Správa plánů.':'')+'</p></div>';
    document.getElementById('content').innerHTML = html;
    return;
  }

  var calHtml = renderScheduleCalendarHtml();

  var listHtml = '';
  var w = scheduleListWindow();
  var occList = allVisibleScheduleOccurrences(w.start, w.end);
  occList = filterScheduleScope(occList);
  var wps = sortedWorkplaces();
  var types = sortedAuditTypes();
  var activeFilterCount = (state.scheduleFilterWp?1:0) + (state.scheduleFilterType?1:0) + (state.scheduleFilterGroup?1:0) + (state.scheduleFilterStatus!=='naplanovano'?1:0);

  listHtml += '<h3 class="set-section" style="margin-top:0">Seznam termínů</h3>';
  listHtml += '<div class="list-toolbar">';
  listHtml += '<div class="field" style="margin-bottom:10px">' +
    '<input id="scheduleSearchInput" value="'+esc(state.scheduleSearch||'')+'" placeholder="Hledat podle pracoviště, plánu, auditora…"/></div>';
  listHtml += '<div class="filter-toggle-row">' +
    '<button type="button" class="filter-toggle-btn'+(state.scheduleFiltersOpen?' on':'')+'" onclick="App.toggleScheduleFiltersOpen()">'+ICONS.filter+' Filtry'+scopeBadgeHtml('schedule')+
    (activeFilterCount>0 ? '<span class="count-badge">'+activeFilterCount+'</span>' : '')+
    '</button>' +
    sortControlHtml('scheduleSortSelect', SCHEDULE_SORT_OPTIONS, state.scheduleSortBy, state.scheduleSortDir, 'toggleScheduleSortDir') +
    '</div>';
  listHtml += '</div>';

  if(state.scheduleFiltersOpen){
    listHtml += '<div class="filter-panel">' + scopeToggleHtml('schedule');
    listHtml += '<div class="filter-grid" style="margin-bottom:10px">' +
      wpFilterFieldsHtml('scheduleWpSelect', state.scheduleFilterWp, wps, 'App.setScheduleWpFilter(%s)') +
      auditGroupFilterFieldHtml('scheduleGroupSelect', state.scheduleFilterGroup) +
      '<div class="field"><label>Druh auditu</label>' +
      '<select class="filter-select" id="scheduleTypeSelect" '+(types.length===0?'disabled':'')+'><option value="">Všechny druhy</option>' +
      typesInGroup(types, state.scheduleFilterGroup).map(function(e){ return '<option value="'+e[0]+'" '+(state.scheduleFilterType===e[0]?'selected':'')+'>'+esc(e[1].name)+'</option>'; }).join('') +
      '</select></div></div>';
    listHtml += '<div style="margin-bottom:0"><label>Stav</label>' +
      '<div class="pill-select" style="margin-bottom:0;margin-top:4px">' +
      [{v:'',l:'Vše'},{v:'po_terminu',l:'Po termínu'},{v:'naplanovano',l:'Naplánováno'},{v:'provedeno',l:'Proveden'},{v:'neprovedeno',l:'Neprovedeno'}].map(function(s){
        return '<button type="button" class="'+(state.scheduleFilterStatus===s.v?'on':'')+'" onclick="App.setScheduleStatusFilter(\''+s.v+'\')">'+s.l+'</button>';
      }).join('') + '</div></div>';
    listHtml += filterActionsHtml('clearScheduleFilters', 'schedule', state.scheduleFilterStatus==='naplanovano' && !state.scheduleFilterWp && !state.scheduleFilterType && !state.scheduleFilterGroup);
    listHtml += '</div>';
  }

  var filteredOcc = filteredScheduleOccurrences(occList);
  var schedSig = JSON.stringify([state.currentCompanyId, w.start.getTime(), w.end.getTime(),
    state.scheduleFilterWp, state.scheduleFilterType, state.scheduleFilterGroup, state.scheduleFilterStatus,
    state.scheduleSearch, state.scheduleSortBy, state.scheduleSortDir]);
  var schedPage = paginateList(filteredOcc, 'schedulePage', 'schedulePageSig', schedSig);
  if(filteredOcc.length===0){
    listHtml += '<div class="empty">'+ICONS.empty+'<p>Žádné termíny neodpovídají filtru.</p></div>';
  } else {
    listHtml += renderScheduleListHtml(schedPage.items, paginationControlsHtml('schedulePage', schedPage));
  }

  html += '<div class="sched-split">' +
    '<div class="sched-split-cal">' + calHtml + '</div>' +
    '<div class="sched-split-list">' + listHtml + '</div>' +
    '</div>';

  document.getElementById('content').innerHTML = html;
  var schedSearchEl = document.getElementById('scheduleSearchInput');
  if(schedSearchEl) schedSearchEl.addEventListener('input', function(){ state.scheduleSearch = schedSearchEl.value; render(); });
  var schedWpSelEl = document.getElementById('scheduleWpSelect');
  if(schedWpSelEl) schedWpSelEl.addEventListener('change', function(){ App.setScheduleWpFilter(schedWpSelEl.value); });
  var schedTypeSelEl = document.getElementById('scheduleTypeSelect');
  if(schedTypeSelEl) schedTypeSelEl.addEventListener('change', function(){ App.setScheduleTypeFilter(schedTypeSelEl.value); });
  var schedGroupSelEl = document.getElementById('scheduleGroupSelect');
  if(schedGroupSelEl) schedGroupSelEl.addEventListener('change', function(){ App.setScheduleGroupFilter(schedGroupSelEl.value); });
  var schedSortSelEl = document.getElementById('scheduleSortSelect');
  if(schedSortSelEl) schedSortSelEl.addEventListener('change', function(){ App.setScheduleSortBy(schedSortSelEl.value); });
}
App.setScheduleWpFilter = function(v){ state.scheduleFilterWp = v; render(); };
App.setScheduleTypeFilter = function(v){ state.scheduleFilterType = v; render(); };
App.setScheduleGroupFilter = function(v){
  state.scheduleFilterGroup = v;
  if(v && state.scheduleFilterType && !matchesAuditGroup(state.scheduleFilterType, v)) state.scheduleFilterType = '';
  render();
};
App.setScheduleDraftGroup = function(g){
  state.scheduleDraftGroup = g || '';
  // druh mimo vybranou skupinu se zruší
  if(g && auditGroupOfType(state.scheduleDraftAuditTypeId)!==g) state.scheduleDraftAuditTypeId = '';
  render();
};
App.toggleSmFilters = function(){ state.smFiltersOpen = !state.smFiltersOpen; render(); };
App.setSmFilter = function(key, v){
  state[key] = v || '';
  if(key==='smFilterGroup' && v && state.smFilterType && auditGroupOfType(state.smFilterType)!==v) state.smFilterType = '';
  render();
};
App.clearSmFilters = function(){ state.smFilterGroup = state.smFilterType = state.smFilterWp = state.smFilterAuditor = state.smFilterStatus = ''; render(); };
App.setScheduleStatusFilter = function(v){ state.scheduleFilterStatus = v; render(); };
App.setScheduleSortBy = function(v){
  var opt = SCHEDULE_SORT_OPTIONS.find(function(o){ return o.id===v; });
  state.scheduleSortBy = v;
  state.scheduleSortDir = opt ? opt.defaultDir : 'asc';
  render();
};
App.toggleScheduleSortDir = function(){
  state.scheduleSortDir = state.scheduleSortDir==='asc' ? 'desc' : 'asc';
  render();
};
App.toggleScheduleFiltersOpen = function(){ state.scheduleFiltersOpen = !state.scheduleFiltersOpen; render(); };
// Z Moje úkoly: Plán s mými termíny po termínu (filtr zabalený).
App.openMyMissedAudits = function(){
  state.scheduleFilterWp = '';
  state.scheduleFilterType = ''; state.scheduleFilterGroup = '';
  state.scheduleFilterStatus = 'po_terminu';
  state.scheduleSearch = '';
  state.scheduleFiltersOpen = false;
  setScopeSilent('schedule', 'mine');
  state.schedulePage = 1;
  state.view = 'schedule';
  render();
};
// toDefault: výchozí nastavení (Moje + Naplánováno = neprovedené v lhůtě i po
// termínu), jinak vše zrušit (Vše).
App.clearScheduleFilters = function(toDefault){
  state.scheduleFilterWp = '';
  state.scheduleFilterType = ''; state.scheduleFilterGroup = '';
  state.scheduleFilterStatus = toDefault ? 'naplanovano' : '';
  setScopeSilent('schedule', toDefault ? 'mine' : 'all');
  render();
};
App.scheduleCalendarShift = function(delta){
  var now = new Date();
  var y = state.scheduleCalendarY!=null ? state.scheduleCalendarY : now.getFullYear();
  var m = state.scheduleCalendarM!=null ? state.scheduleCalendarM : now.getMonth();
  m += delta;
  if(m<0){ m=11; y--; } else if(m>11){ m=0; y++; }
  state.scheduleCalendarY = y; state.scheduleCalendarM = m;
  state.scheduleCalendarSelectedDay = null;
  render();
};
App.selectScheduleCalendarDay = function(dateStr){
  state.scheduleCalendarSelectedDay = (state.scheduleCalendarSelectedDay===dateStr) ? null : dateStr;
  render();
};

// ---------- Správa plánů (CRUD, jen Administrátor/Správce) ----------
// Obecný "Všechny X / jen vybrané" picker — chips se zvolenými položkami +
// dropdown "+ přidat", kde první nabízená možnost je "Všechny X" (jejím
// výběrem se zruší konkrétní výběr). Stejný vzor jako skupina auditorů,
// jen bez pořadí/rotace (na pořadí tady nezáleží).
function scheduleMultiPickerHtml(cfg){
  var selected = cfg.selectedIds || [];
  var html = '<div class="field" style="margin-bottom:12px"><label>'+esc(cfg.label)+'</label>';
  if(selected.length===0){
    html += '<div class="sched-chip" style="margin-bottom:8px;display:inline-flex"><b>'+esc(cfg.allLabel)+'</b></div><br/>';
  } else {
    html += '<div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:8px">';
    selected.forEach(function(id){
      html += '<div class="sched-chip">' +
        '<span>'+esc(cfg.labelFor(id))+'</span>' +
        '<button type="button" onclick="App.scheduleMultiRemove(\''+cfg.key+'\',\''+id+'\')" title="Odebrat">×</button>' +
        '</div>';
    });
    html += '</div>';
  }
  var remaining = cfg.candidates.filter(function(e){ return selected.indexOf(e[0])===-1; });
  html += '<div class="add-row"><select id="'+cfg.selectId+'"><option value="__ALL__">'+esc(cfg.allLabel)+'</option>' +
    remaining.map(function(e){ return '<option value="'+e[0]+'">'+esc(cfg.labelFor(e[0])||e[1].name)+'</option>'; }).join('') +
    '</select><button type="button" class="btn" onclick="App.scheduleMultiAdd(\''+cfg.key+'\',\''+cfg.selectId+'\')">Přidat</button></div>' +
    '</div>';
  return html;
}
// Pracoviště plánu (rozhodnutí uživatele 2026-11-03): pole „Pracoviště“ (první
// úroveň) a pod ním „Podřízená pracoviště“ z vybraných. Uloženo jako jeden seznam
// workplaceIds: nadřazené bez vybraných podřízených = celé pracoviště, jinak jen
// vybraná podřízená (nadřazené v seznamu není). Prázdný = všechna první úrovně.
function schedWpSplit(ids, candidates){
  var has = {}; candidates.forEach(function(e){ has[e[0]] = e[1]; });
  var tops = [], kids = [];
  (ids||[]).forEach(function(id){
    var w = has[id] || state.workplaces.get(id);
    if(!w) return;
    if(w.parentId && state.workplaces.has(w.parentId)){
      kids.push(id);
      if(tops.indexOf(w.parentId)<0) tops.push(w.parentId);
    } else if(tops.indexOf(id)<0) tops.push(id);
  });
  return {tops:tops, kids:kids};
}
function scheduleWorkplacePickerHtml(candidates){
  var split = schedWpSplit(state.scheduleDraftWorkplaceIds, candidates);
  var isTop = function(e){ return !e[1].parentId || !state.workplaces.has(e[1].parentId); };
  var html = '<div class="field" style="margin-bottom:12px"><label>Pracoviště</label>';
  if(!split.tops.length){
    html += '<div class="sched-chip" style="margin-bottom:8px;display:inline-flex"><b>Všechna pracoviště</b></div><br/>';
  } else {
    html += '<div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:8px">' + split.tops.map(function(id){
      return '<div class="sched-chip"><span>'+esc((state.workplaces.get(id)||{}).name||'?')+'</span>' +
        '<button type="button" onclick="App.schedWpRemoveTop(\''+id+'\')" title="Odebrat">×</button></div>';
    }).join('') + '</div>';
  }
  html += '<div class="add-row"><select id="schedAddWorkplace"><option value="__ALL__">Všechna pracoviště</option>' +
    candidates.filter(function(e){ return isTop(e) && split.tops.indexOf(e[0])<0; }).map(function(e){ return '<option value="'+e[0]+'">'+esc(e[1].name||'')+'</option>'; }).join('') +
    '</select><button type="button" class="btn" onclick="App.schedWpAddTop()">Přidat</button></div></div>';
  var kidCands = candidates.filter(function(e){ return !isTop(e) && split.tops.indexOf(e[1].parentId)>=0; });
  if(kidCands.length){
    html += '<div class="field" style="margin-bottom:12px"><label>Podřízená pracoviště (nepovinné)</label>';
    if(split.kids.length){
      html += '<div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:8px">' + split.kids.map(function(id){
        return '<div class="sched-chip"><span>'+esc(workplaceLabel(id))+'</span>' +
          '<button type="button" onclick="App.schedWpRemoveKid(\''+id+'\')" title="Odebrat">×</button></div>';
      }).join('') + '</div>';
    }
    var rest = kidCands.filter(function(e){ return split.kids.indexOf(e[0])<0; });
    if(rest.length){
      html += '<div class="add-row"><select id="schedAddSubWorkplace"><option value="">Vyberte podřízené…</option>' +
        rest.map(function(e){ return '<option value="'+e[0]+'">'+esc(workplaceLabel(e[1]))+'</option>'; }).join('') +
        '</select><button type="button" class="btn" onclick="App.schedWpAddKid()">Přidat</button></div>';
    }
    html += '<div class="set-note" style="margin:6px 0 0">Pracoviště, u kterého podřízené nevyberete, se audituje celé. Vyberete-li podřízená, audituje se jen na nich.</div></div>';
  }
  return html;
}
function schedWpJoin(tops, kids){
  // nadřazené bez vybraných podřízených + vybraná podřízená
  var out = [];
  tops.forEach(function(t){
    var mine = kids.filter(function(k){ return (state.workplaces.get(k)||{}).parentId===t; });
    if(mine.length) out = out.concat(mine); else out.push(t);
  });
  return out;
}
function schedWpSet(tops, kids){
  state.scheduleDraftWorkplaceIds = schedWpJoin(tops, kids);
  render();
}
App.schedWpAddTop = function(){
  var v = (document.getElementById('schedAddWorkplace')||{}).value;
  var sp = schedWpSplit(state.scheduleDraftWorkplaceIds, []);
  if(v==='__ALL__'){ state.scheduleDraftWorkplaceIds = []; render(); return; }
  if(v && sp.tops.indexOf(v)<0) sp.tops.push(v);
  schedWpSet(sp.tops, sp.kids);
};
App.schedWpRemoveTop = function(id){
  var sp = schedWpSplit(state.scheduleDraftWorkplaceIds, []);
  schedWpSet(sp.tops.filter(function(t){ return t!==id; }), sp.kids.filter(function(k){ return (state.workplaces.get(k)||{}).parentId!==id; }));
};
App.schedWpAddKid = function(){
  var v = (document.getElementById('schedAddSubWorkplace')||{}).value;
  if(!v) return;
  var sp = schedWpSplit(state.scheduleDraftWorkplaceIds, []);
  if(sp.kids.indexOf(v)<0) sp.kids.push(v);
  schedWpSet(sp.tops, sp.kids);
};
App.schedWpRemoveKid = function(id){
  var sp = schedWpSplit(state.scheduleDraftWorkplaceIds, []);
  schedWpSet(sp.tops, sp.kids.filter(function(k){ return k!==id; }));
};
function scheduleEditFormHtml(id){
  var isNew = id==='new';
  var effCompanyIds = scheduleEffectiveCompanyIds(state.scheduleDraftCompanyIds);
  var branchCandidates = scheduleBranchCandidates(effCompanyIds);
  var effBranchIds = scheduleEffectiveBranchIds(state.scheduleDraftBranchIds, effCompanyIds);
  var wpCandidates = scheduleWorkplaceCandidates(effCompanyIds, effBranchIds);
  var types = scheduleAuditTypeCandidates(effCompanyIds);
  var candidates = usersWithCompanyAccess(effCompanyIds);
  // Formulář je rozdělený do sekcí jako ostatní stránky Nastavení:
  // Co a kde / Kdy / Kdo / Stav, tlačítko Uložit pod nimi.
  var html = settingsSectionHtml('Co a kde') + '<div class="card">';

  html += '<div class="field" style="margin-bottom:12px"><label>Název plánu (nepovinné)</label>' +
    '<input id="schedDraftName" type="text" value="'+esc(state.scheduleDraftName||'')+'" placeholder="např. Požární kontrola – týdenní" oninput="App.setScheduleDraft(\'scheduleDraftName\', this.value)"/></div>';

  // Nejdřív skupina auditů, pak druh jen z ní (rozhodnutí uživatele 2026-10-31).
  var draftGroup = (state.scheduleDraftGroup!=null) ? state.scheduleDraftGroup : auditGroupOfType(state.scheduleDraftAuditTypeId);
  var groupTypes = typesInGroup(types, draftGroup);
  html += auditGroupPickHtml('schedDraftGroup', draftGroup, 'App.setScheduleDraftGroup(this.value)');
  html += '<div class="field" style="margin-bottom:12px"><label>Druh auditu</label>' +
    '<select id="schedDraftType" onchange="App.setScheduleDraftRender(\'scheduleDraftAuditTypeId\', this.value)">' +
    '<option value="">Vyberte druh auditu…</option>' +
    groupTypes.map(function(e){ return '<option value="'+e[0]+'" '+(state.scheduleDraftAuditTypeId===e[0]?'selected':'')+'>'+esc(e[1].name)+'</option>'; }).join('') +
    '</select></div>';

  // Plán patří vždy aktivní firmě (rozhodnutí uživatele) — výběr právního
  // subjektu ve formuláři není; companyIds se nastaví při založení plánu.
  // Sídlo firmy (branchId "") je vždy nabízeno jako samostatná "provozovna" —
  // workplacesForCompany/branchLabel ji už tak chápou, tady ji jen přidáme
  // do nabídky, pokud tam ještě není.
  var branchCandidatesWithSidlo = [['', {name:'Sídlo firmy', order:-1}]].concat(branchCandidates);
  html += scheduleMultiPickerHtml({
    key:'scheduleDraftBranchIds', selectId:'schedAddBranch', label:'Provozovny', allLabel:'Všechny provozovny',
    selectedIds: state.scheduleDraftBranchIds, candidates: branchCandidatesWithSidlo,
    labelFor: function(id){ return branchLabel(id); }
  });
  html += scheduleWorkplacePickerHtml(wpCandidates);
  html += '</div>';

  html += settingsSectionHtml('Kdy') + '<div class="card">';
  html += '<div class="field-row" style="margin-bottom:12px">' +
    '<div class="field" style="margin-bottom:0"><label>Opakovat každých</label>' +
    '<input id="schedDraftFreqValue" type="number" min="1" value="'+esc(state.scheduleDraftFreqValue)+'" oninput="App.setScheduleDraft(\'scheduleDraftFreqValue\', this.value)"/></div>' +
    '<div class="field" style="margin-bottom:0"><label>Jednotka</label>' +
    '<select id="schedDraftFreqUnit" onchange="App.setScheduleDraft(\'scheduleDraftFreqUnit\', this.value)">' +
    [['days','dní'],['weeks','týdnů'],['months','měsíců'],['years','let']].map(function(o){
      return '<option value="'+o[0]+'" '+(state.scheduleDraftFreqUnit===o[0]?'selected':'')+'>'+o[1]+'</option>';
    }).join('') +
    '</select></div></div>';

  html += '<div class="field-row" style="margin-bottom:12px">' +
    '<div class="field" style="margin-bottom:0"><label>Datum prvního termínu</label>' +
    dateTimeInputHtml({id:'schedDraftStartDate', value:state.scheduleDraftStartDate, onChange:'App.setScheduleDraft(\'scheduleDraftStartDate\', this.value)'}) + '</div>' +
    '<div class="field" style="margin-bottom:0"><label>Čas (nepovinné)</label>' +
    dateTimeInputHtml({type:'time', id:'schedDraftTime', value:state.scheduleDraftTime, onChange:'App.setScheduleDraft(\'scheduleDraftTime\', this.value)'}) + '</div></div>' +
    '<div class="muted" style="font-size:var(--fs-sm);margin:-6px 0 12px">Bez vyplnění se termín propíše do kalendáře jako celodenní událost. S časem se do kalendáře propíše hodinová událost od tohoto času.</div>';

  // Obchůzka může projít všechna pracoviště plánu v jednom auditu.
  var multiOn = isFindingsTypeId(state.scheduleDraftAuditTypeId) && !!state.scheduleDraftMultiWorkplace;
  html += '<div class="field" style="margin-bottom:0">' +
    (isFindingsTypeId(state.scheduleDraftAuditTypeId) ? settingsSwitchRowHtml('schedDraftMultiWorkplace', 'Všechna pracoviště v jednom auditu',
      'Obchůzka projde všechna vybraná pracoviště najednou; u každého zjištění se vybere, na kterém z nich bylo.',
      multiOn, 'App.setScheduleDraftRender(\'scheduleDraftMultiWorkplace\', this.checked)') : '') +
    (multiOn ? '' : settingsSwitchRowHtml('schedDraftSpreadWorkplaces', 'Rozložit pracoviště do jednotlivých termínů',
      'V jednom kole se audituje jen jedno pracoviště a pracoviště se mezi koly střídají. Jinak se audituje vše najednou ve stejný den.',
      state.scheduleDraftSpreadWorkplaces, 'App.setScheduleDraft(\'scheduleDraftSpreadWorkplaces\', this.checked)')) +
    // Přepínač je obrácený k uloženému skipWeekends (rozhodnutí uživatele:
    // výchozí = víkendy se neplánují, zapnout se dá plánování i na víkend).
    settingsSwitchRowHtml('schedDraftAllowWeekends', 'Plánovat termíny i na víkendy',
      'Vypnuto: u plánu po dnech se počítají jen pracovní dny a u plánu po týdnech a měsících se termín ze soboty nebo neděle posune na pondělí.',
      !state.scheduleDraftSkipWeekends, 'App.setScheduleDraft(\'scheduleDraftSkipWeekends\', !this.checked)') + '</div>';

  html += '</div>';

  html += settingsSectionHtml('Kdo') + '<div class="card">';
  html += '<div class="field" style="margin-bottom:0"><label>Auditoři (pořadí rotace)</label>';
  if(state.scheduleDraftAuditorIds.length===0){
    html += '<p class="muted" style="font-size:var(--fs-base);margin:4px 0 8px">Zatím žádný auditor. Bez auditora se termín jen zobrazí v plánu, bez e-mailového upozornění.</p>';
  } else {
    html += '<div style="display:flex;flex-direction:column;gap:6px;margin-bottom:8px">';
    state.scheduleDraftAuditorIds.forEach(function(entry, idx){
      html += '<div class="sched-chip" style="justify-content:space-between">' +
        '<span>'+(idx+1)+'. '+esc(scheduleAuditorEntryLabel(entry))+'</span>' +
        '<span style="display:flex;gap:4px">' +
        (idx>0 ? '<button type="button" onclick="App.moveScheduleAuditor('+idx+',-1)" title="Posunout výš">'+ICONS.up+'</button>' : '') +
        (idx<state.scheduleDraftAuditorIds.length-1 ? '<button type="button" onclick="App.moveScheduleAuditor('+idx+',1)" title="Posunout níž">'+ICONS.down+'</button>' : '') +
        '<button type="button" onclick="App.removeScheduleAuditor(\''+entry+'\')" title="Odebrat">×</button>' +
        '</span></div>';
    });
    html += '</div>';
  }
  var available = candidates.filter(function(e){ return state.scheduleDraftAuditorIds.indexOf(e[0])===-1; });
  if(available.length){
    html += '<div class="add-row"><select id="schedAddAuditor"><option value="">+ přidat osobu…</option>' +
      available.map(function(e){ return '<option value="'+e[0]+'">'+esc(e[1])+'</option>'; }).join('') +
      '</select><button type="button" class="btn" onclick="App.addScheduleAuditorFromSelect()">Přidat</button></div>';
  } else if(candidates.length===0){
    html += '<p class="muted" style="font-size:var(--fs-base)">K vybraným subjektům zatím nemá přístup žádný uživatel aplikace s loginem.</p>';
  }
  var availableGroups = sortedAuditorGroups().filter(function(e){ return state.scheduleDraftAuditorIds.indexOf(auditorGroupEntryFor(e[0]))===-1; });
  if(availableGroups.length){
    html += '<div class="add-row" style="margin-top:8px"><select id="schedAddAuditorGroup"><option value="">+ přidat skupinu…</option>' +
      availableGroups.map(function(e){ return '<option value="'+e[0]+'">'+esc(e[1].name||'(bez názvu)')+'</option>'; }).join('') +
      '</select><button type="button" class="btn" onclick="App.addScheduleAuditorGroupFromSelect()">Přidat</button></div>';
  }
  html += '</div>';
  // Spoluauditoři: jdou s auditorem na každý termín (zúčastněné osoby auditu).
  var coIds = state.scheduleDraftCoAuditorIds || [];
  html += '<div class="field" style="margin:14px 0 0"><label>Spoluauditoři (nepovinné)</label>';
  if(coIds.length){
    html += '<div class="pp-chips">' + coIds.map(function(id){
      return '<span class="sched-chip">'+esc(scheduleAuditorEntryLabel(id))+'<button type="button" title="Odebrat" onclick="App.removeScheduleCoAuditor(\''+id+'\')">×</button></span>';
    }).join('') + '</div>';
  }
  // Auditor plánu nemůže být zároveň spoluauditorem (ze skupiny se v termínu vynechá sám).
  var coAvailable = candidates.filter(function(e){ return coIds.indexOf(e[0])===-1 && state.scheduleDraftAuditorIds.indexOf(e[0])===-1; });
  if(coAvailable.length){
    html += '<div class="add-row"><select id="schedAddCoAuditor"><option value="">+ přidat spoluauditora…</option>' +
      coAvailable.map(function(e){ return '<option value="'+e[0]+'">'+esc(e[1])+'</option>'; }).join('') +
      '</select><button type="button" class="btn" onclick="App.addScheduleCoAuditorFromSelect()">Přidat</button></div>';
  }
  var coGroups = sortedAuditorGroups().filter(function(e){ return coIds.indexOf(auditorGroupEntryFor(e[0]))===-1; });
  if(coGroups.length){
    html += '<div class="add-row" style="margin-top:8px"><select id="schedAddCoAuditorGroup"><option value="">+ přidat skupinu auditorů…</option>' +
      coGroups.map(function(e){ return '<option value="'+e[0]+'">'+esc(e[1].name||'(bez názvu)')+'</option>'; }).join('') +
      '</select><button type="button" class="btn" onclick="App.addScheduleCoAuditorGroupFromSelect()">Přidat</button></div>';
  }
  html += '<div class="set-note" style="margin:6px 0 0">Jdou s auditorem na každý termín: mají termín ve svém kalendáři a připomenutí e-mailem, při zahájení se doplní jako zúčastněné osoby. Auditor termínu se ze skupiny nevypisuje.</div></div>';

  html += '</div>';

  html += settingsSectionHtml('Stav') + '<div class="card">';
  html += '<div class="field" style="margin-bottom:0">' + settingsSwitchRowHtml('schedDraftActive', 'Plán je aktivní', 'Neaktivní plán se nezobrazuje v Plánu auditů ani v kalendáři.',
    state.scheduleDraftActive, 'App.setScheduleDraft(\'scheduleDraftActive\', this.checked)') + '</div>';

  html += '</div>';
  html += '<button class="btn btn-primary btn-block" style="margin-top:16px" onclick="App.saveSchedule(\''+id+'\')">'+(isNew?'Vytvořit plán':'Uložit')+'</button>';
  return html;
}
function renderScheduleManage(){
  ensureRoleProfiles();
  var html = settingsHeadHtml('Plány auditů', 'Jak často se který audit opakuje a kdo ho provádí.', 'schedule');

  if(state.editScheduleId){
    var schedCo = state.companies.get((state.scheduleDraftCompanyIds||[])[0] || state.currentCompanyId);
    html = settingsHeadHtml(state.editScheduleId==='new' ? 'Nový plán auditu' : 'Upravit plán auditu', schedCo ? ('Firma '+schedCo.name) : 'Jak často se audit opakuje, kde a kdo ho provádí.', null, 'App.cancelEditSchedule()');
    html += scheduleEditFormHtml(state.editScheduleId);
    document.getElementById('content').innerHTML = '<div class="content-narrow">' + html + '</div>';
    return;
  }

  html += '<button class="btn btn-primary btn-block" onclick="App.startNewSchedule()">'+ICONS.plus+' Nový plán auditu</button>';

  // Jen plány, které se alespoň částečně dotýkají subjektů, které vidím
  // (prázdné companyIds = "Všechny" vždy vyhovuje), A navíc — stejně jako
  // přehled termínů v "Plán auditů" (allVisibleScheduleOccurrences) — jen
  // plány, které se vztahují k právě vybranému subjektu v přepínači firem.
  // Plán vázaný výhradně na jiný subjekt se sem nemíchá, dokud se na něj
  // nepřepne; v databázi samozřejmě zůstává beze změny.
  var list = Array.from(state.schedules.entries()).filter(function(e){
    var cids = e[1].companyIds;
    if(Array.isArray(cids) && cids.length>0){
      if(!cids.some(isCompanyAccessible)) return false;
      if(state.currentCompanyId && cids.indexOf(state.currentCompanyId)===-1) return false;
    }
    return true;
  });
  // Vyhledávání a filtry (rozhodnutí uživatele 2026-10-31): Skupina, Druh,
  // Pracoviště, Auditor, Stav; hledání v názvu, druhu, pracovištích a auditorech.
  var allCount = list.length;
  var smTypes = sortedAuditTypes(), smWps = sortedWorkplaces();
  var smAuditors = usersWithCompanyAccess([state.currentCompanyId||'']);
  var smActive = (state.smFilterGroup?1:0)+(state.smFilterType?1:0)+(state.smFilterWp?1:0)+(state.smFilterAuditor?1:0)+(state.smFilterStatus?1:0);
  if(allCount>0){
    html += '<div class="list-toolbar" style="margin-top:12px">' +
      '<div class="field" style="margin-bottom:10px"><input id="smSearchInput" value="'+esc(state.smSearch||'')+'" placeholder="Hledat podle názvu, druhu, pracoviště, auditora…"/></div>' +
      '<div class="filter-toggle-row"><button type="button" class="filter-toggle-btn'+(state.smFiltersOpen?' on':'')+'" onclick="App.toggleSmFilters()">'+ICONS.filter+' Filtry' +
      (smActive ? '<span class="count-badge">'+smActive+'</span>' : '') + '</button></div></div>';
    if(state.smFiltersOpen){
      html += '<div class="filter-panel"><div class="filter-grid" style="margin-bottom:10px">' +
        auditGroupFilterFieldHtml('smGroupSelect', state.smFilterGroup||'') +
        '<div class="field"><label>Druh auditu</label><select class="filter-select" id="smTypeSelect"><option value="">Všechny druhy</option>' +
        typesInGroup(smTypes, state.smFilterGroup||'').map(function(e){ return '<option value="'+e[0]+'" '+(state.smFilterType===e[0]?'selected':'')+'>'+esc(e[1].name)+'</option>'; }).join('') + '</select></div>' +
        wpFilterFieldsHtml('smWpSelect', state.smFilterWp, smWps, "App.setSmFilter('smFilterWp', %s)") +
        '<div class="field"><label>Auditor</label><select class="filter-select" id="smAuditorSelect"><option value="">Všichni auditoři</option>' +
        smAuditors.map(function(e){ return '<option value="'+e[0]+'" '+(state.smFilterAuditor===e[0]?'selected':'')+'>'+esc(e[1])+'</option>'; }).join('') + '</select></div>' +
        '</div><div><label>Stav</label><div class="pill-select" style="margin:4px 0 0">' +
        [['','Vše'],['active','Aktivní'],['inactive','Neaktivní']].map(function(o){ return '<button type="button" class="'+((state.smFilterStatus||'')===o[0]?'on':'')+'" onclick="App.setSmFilter(\'smFilterStatus\',\''+o[0]+'\')">'+o[1]+'</button>'; }).join('') +
        '</div></div>' +
        filterActionsHtml('clearSmFilters', '', !smActive) +
        '</div>';
    }
    var smQ = normalizeSearchText(state.smSearch||'');
    list = list.filter(function(e){
      var s = e[1];
      if(state.smFilterStatus==='active' && s.active===false) return false;
      if(state.smFilterStatus==='inactive' && s.active!==false) return false;
      if(state.smFilterType && s.auditTypeId!==state.smFilterType) return false;
      if(state.smFilterGroup && !matchesAuditGroup(s.auditTypeId, state.smFilterGroup)) return false;
      if(!wpFilterMatchesAny(state.smFilterWp, scheduleMatchedWorkplaces(s).map(function(w){ return w[0]; }))) return false;
      if(state.smFilterAuditor && scheduleResolvedAuditorIds(s).indexOf(state.smFilterAuditor)<0) return false;
      if(smQ){
        var at = state.auditTypes.get(s.auditTypeId) || {};
        var hay = [s.name, at.name, at.auditGroup, scheduleScopeLabel(s,'branchIds'), scheduleScopeLabel(s,'workplaceIds')]
          .concat(scheduleMatchedWorkplaces(s).map(function(w){ return w[1].name; }))
          .concat((Array.isArray(s.auditorIds)?s.auditorIds:[]).map(scheduleAuditorEntryLabel)).join(' ');
        if(normalizeSearchText(hay).indexOf(smQ)<0) return false;
      }
      return true;
    });
  }
  if(allCount===0){
    html += '<div class="empty">'+ICONS.empty+'<p>Zatím žádný plán auditu.</p></div>';
  } else if(list.length===0){
    html += '<div class="card" style="margin-top:12px"><div class="muted" style="font-size:var(--fs-base)">Hledání ani filtrům neodpovídá žádný plán.</div></div>';
  } else {
    var smPage = paginateList(list, 'scheduleManagePage', 'scheduleManagePageSig', JSON.stringify([state.smSearch, state.smFilterGroup, state.smFilterType, state.smFilterWp, state.smFilterAuditor, state.smFilterStatus]));
    html += '<div class="card" style="margin-top:12px">';
    smPage.items.forEach(function(e, i){
      var id = e[0], s = e[1];
      var at = state.auditTypes.get(s.auditTypeId) || {};
      var matchedCount = scheduleMatchedWorkplaces(s).length;
      var auditorNames = (Array.isArray(s.auditorIds)?s.auditorIds:[]).map(scheduleAuditorEntryLabel).join(', ');
      var confirm = confirmRow('sched:'+id, 'Smazat plán auditu "'+(at.name||'')+'" ('+matchedCount+' pracovišť)?');
      html += (i>0?'<div class="list-sep"></div>':'');
      if(confirm){
        html += confirm;
      } else {
        html += '<div class="list-item">' +
          '<div style="min-width:0">' +
          '<div style="font-weight:600;font-size:var(--fs-md)">'+esc(s.name||at.name||'(druh auditu?)')+
          (s.active===false?' <span class="muted" style="font-weight:500;font-size:var(--fs-sm)">· neaktivní</span>':'')+'</div>' +
          (s.name ? '<div class="muted" style="font-size:var(--fs-sm)">'+esc(at.name||'(druh auditu?)')+'</div>' : '') +
          ((Array.isArray(s.companyIds) && s.companyIds.length===1) ? '' : '<div class="muted" style="font-size:var(--fs-sm)">'+esc(scheduleScopeLabel(s,'companyIds'))+'</div>') +
          '<div class="muted" style="font-size:var(--fs-sm)">'+esc(scheduleScopeLabel(s,'branchIds'))+' · '+esc(scheduleScopeLabel(s,'workplaceIds'))+'</div>' +
          '<div class="muted" style="font-size:var(--fs-sm)">'+matchedCount+' pracovišť · každých '+(s.freqValue||1)+' '+FREQ_UNIT_LABELS[s.freqUnit||'months']+'</div>' +
          '<div class="muted" style="font-size:var(--fs-sm)">'+(auditorNames?('Auditoři: '+esc(auditorNames)):'bez auditora')+'</div>' +
          '</div>' +
          '<div style="display:flex;gap:2px;flex-shrink:0">' +
          '<button class="icon-btn" title="Upravit" onclick="App.editSchedule(\''+id+'\')">'+ICONS.edit+'</button>' +
          '<button class="icon-btn" title="Smazat" onclick="App.askConfirm(\'sched:'+id+'\', function(){App.deleteSchedule(\''+id+'\')})">'+ICONS.trash+'</button>' +
          '</div></div>';
      }
    });
    html += '</div>' + paginationControlsHtml('scheduleManagePage', smPage);
  }
  document.getElementById('content').innerHTML = '<div class="content-narrow">' + html + '</div>';
  var smS = document.getElementById('smSearchInput');
  if(smS) smS.addEventListener('input', function(){ state.smSearch = smS.value; render(); });
  [['smGroupSelect','smFilterGroup'],['smTypeSelect','smFilterType'],['smWpSelect','smFilterWp'],['smAuditorSelect','smFilterAuditor']].forEach(function(p){
    var el = document.getElementById(p[0]);
    if(el) el.addEventListener('change', function(){ App.setSmFilter(p[1], el.value); });
  });
}
App.startNewSchedule = function(){
  if(!canManageContent()) return;
  if(!state.currentCompanyId || !isCompanyAccessible(state.currentCompanyId)){ showToast('Nejdřív vyberte firmu v horním panelu.'); return; }
  state.editScheduleId = 'new';
  state.scheduleDraftCompanyIds = [state.currentCompanyId];
  state.scheduleDraftBranchIds = [];
  state.scheduleDraftWorkplaceIds = [];
  state.scheduleDraftAuditTypeId = '';
  state.scheduleDraftGroup = null;
  state.scheduleDraftStartDate = '';
  state.scheduleDraftTime = '';
  state.scheduleDraftFreqValue = 3;
  state.scheduleDraftFreqUnit = 'months';
  state.scheduleDraftAuditorIds = [];
  state.scheduleDraftActive = true;
  state.scheduleDraftSpreadWorkplaces = false;
  state.scheduleDraftMultiWorkplace = false;
  state.scheduleDraftCoAuditorIds = [];
  state.scheduleDraftSkipWeekends = true;
  state.scheduleDraftName = '';
  state.confirmTarget = '';
  render();
};
App.editSchedule = function(id){
  if(!canManageContent()) return;
  var s = state.schedules.get(id);
  if(!s) return;
  state.editScheduleId = id;
  state.scheduleDraftCompanyIds = Array.isArray(s.companyIds) ? s.companyIds.slice() : [];
  state.scheduleDraftBranchIds = Array.isArray(s.branchIds) ? s.branchIds.slice() : [];
  state.scheduleDraftWorkplaceIds = Array.isArray(s.workplaceIds) ? s.workplaceIds.slice() : [];
  (function(){ var sp = schedWpSplit(state.scheduleDraftWorkplaceIds, []); state.scheduleDraftWorkplaceIds = schedWpJoin(sp.tops, sp.kids); })();
  state.scheduleDraftAuditTypeId = s.auditTypeId || '';
  state.scheduleDraftGroup = null;
  state.scheduleDraftStartDate = s.startDate || '';
  state.scheduleDraftTime = s.time || '';
  state.scheduleDraftFreqValue = s.freqValue || 3;
  state.scheduleDraftFreqUnit = s.freqUnit || 'months';
  state.scheduleDraftAuditorIds = Array.isArray(s.auditorIds) ? s.auditorIds.slice() : [];
  state.scheduleDraftActive = s.active!==false;
  state.scheduleDraftSpreadWorkplaces = !!s.spreadWorkplaces;
  state.scheduleDraftMultiWorkplace = !!s.multiWorkplace;
  state.scheduleDraftCoAuditorIds = Array.isArray(s.coAuditorIds) ? s.coAuditorIds.slice() : [];
  state.scheduleDraftSkipWeekends = !!s.skipWeekends;
  state.scheduleDraftName = s.name || '';
  state.confirmTarget = '';
  render();
};
App.cancelEditSchedule = function(){
  state.editScheduleId = null;
  render();
};
// Inline onXXX="state.foo=this.value" attributes can't actually see the
// "state" variable — it's local to this module's IIFE, not global, so the
// browser-compiled handler function has no way to resolve it (confirmed:
// it throws "state is not defined" the moment it fires, silently leaving
// the field never actually written to state). Every draft-field input
// below must go through this App-level setter instead — see also
// App.setStateField, the same fix applied to a couple of pre-existing
// inputs ("Uživatelé a role" → nový uživatel) that had the identical bug.
App.setScheduleDraft = function(key, value){
  state[key] = value;
};
App.addScheduleCoAuditorFromSelect = function(){
  var sel = document.getElementById('schedAddCoAuditor');
  if(!sel || !sel.value) return;
  state.scheduleDraftCoAuditorIds = (state.scheduleDraftCoAuditorIds||[]).concat([sel.value]);
  render();
};
App.addScheduleCoAuditorGroupFromSelect = function(){
  var sel = document.getElementById('schedAddCoAuditorGroup');
  if(!sel || !sel.value) return;
  state.scheduleDraftCoAuditorIds = (state.scheduleDraftCoAuditorIds||[]).concat([auditorGroupEntryFor(sel.value)]);
  render();
};
App.removeScheduleCoAuditor = function(id){
  state.scheduleDraftCoAuditorIds = (state.scheduleDraftCoAuditorIds||[]).filter(function(x){ return x!==id; });
  render();
};
App.setScheduleDraftRender = function(key, value){
  state[key] = value;
  render();
};
// "Všechny X / jen vybrané" picker (subjekty/provozovny/pracoviště) —
// volba "Všechny" nastaví příslušné pole zpátky na [], konkrétní položka
// se přidá do pole. Změna subjektů/provozoven zruší níže navazující
// (užší) výběr, protože by jinak mohl ukazovat na položku mimo nový
// rozsah.
App.scheduleMultiAdd = function(key, selectId){
  var sel = document.getElementById(selectId);
  var val = sel && sel.value;
  // Pozor: "" je platná hodnota pro "Sídlo firmy" (branchId ""), ne
  // "nic není vybráno" — select vždy obsahuje aspoň volbu "__ALL__",
  // takže val je null jen když element samotný neexistuje.
  if(val==null) return;
  if(val==='__ALL__'){
    state[key] = [];
  } else if(state[key].indexOf(val)===-1){
    state[key] = state[key].concat([val]);
  }
  if(key==='scheduleDraftCompanyIds'){ state.scheduleDraftBranchIds = []; state.scheduleDraftWorkplaceIds = []; }
  if(key==='scheduleDraftBranchIds'){ state.scheduleDraftWorkplaceIds = []; }
  render();
};
App.scheduleMultiRemove = function(key, id){
  state[key] = state[key].filter(function(v){ return v!==id; });
  if(key==='scheduleDraftCompanyIds'){ state.scheduleDraftBranchIds = []; state.scheduleDraftWorkplaceIds = []; }
  if(key==='scheduleDraftBranchIds'){ state.scheduleDraftWorkplaceIds = []; }
  render();
};
App.addScheduleAuditorFromSelect = function(){
  var sel = document.getElementById('schedAddAuditor');
  var val = sel && sel.value;
  if(!val) return;
  if(state.scheduleDraftAuditorIds.indexOf(val)===-1) state.scheduleDraftAuditorIds.push(val);
  // auditor nemůže být zároveň spoluauditorem
  if((state.scheduleDraftCoAuditorIds||[]).indexOf(val)>=0){
    state.scheduleDraftCoAuditorIds = state.scheduleDraftCoAuditorIds.filter(function(x){ return x!==val; });
    showToast('Auditor nemůže být zároveň spoluauditorem: '+personDisplayName(val)+' už mezi spoluauditory není.');
  }
  render();
};
App.addScheduleAuditorGroupFromSelect = function(){
  var sel = document.getElementById('schedAddAuditorGroup');
  var val = sel && sel.value;
  if(!val) return;
  var entry = auditorGroupEntryFor(val);
  if(state.scheduleDraftAuditorIds.indexOf(entry)===-1) state.scheduleDraftAuditorIds.push(entry);
  render();
};
App.removeScheduleAuditor = function(uid){
  var idx = state.scheduleDraftAuditorIds.indexOf(uid);
  if(idx!==-1) state.scheduleDraftAuditorIds.splice(idx,1);
  render();
};
App.moveScheduleAuditor = function(idx, dir){
  var arr = state.scheduleDraftAuditorIds;
  var j = idx+dir;
  if(j<0 || j>=arr.length) return;
  var tmp = arr[idx]; arr[idx] = arr[j]; arr[j] = tmp;
  render();
};
App.saveSchedule = function(id){
  if(!canManageContent()) return;
  var auditTypeId = state.scheduleDraftAuditTypeId;
  var startDate = state.scheduleDraftStartDate;
  if(!auditTypeId || !startDate){ showToast('Vyplňte druh auditu a datum prvního termínu.'); return; }
  // Obranná kontrola — vybrané subjekty musí být mezi dostupnými (prázdné pole = "Všechny", vždy v pořádku).
  var companyIds = (state.scheduleDraftCompanyIds||[]).filter(isCompanyAccessible);
  if((state.scheduleDraftCompanyIds||[]).length>0 && companyIds.length===0){ showToast('K vybraným subjektům nemáte přístup.'); return; }
  var freqValue = Math.max(1, parseInt(state.scheduleDraftFreqValue,10)||1);
  var freqUnit = state.scheduleDraftFreqUnit || 'months';
  var multiWorkplace = isFindingsTypeId(auditTypeId) && !!state.scheduleDraftMultiWorkplace;
  var data = {
    companyIds: companyIds, branchIds: (state.scheduleDraftBranchIds||[]).slice(), workplaceIds: (state.scheduleDraftWorkplaceIds||[]).slice(),
    auditTypeId: auditTypeId,
    startDate: startDate, time: (state.scheduleDraftTime||'').trim(), freqValue: freqValue, freqUnit: freqUnit,
    auditorIds: state.scheduleDraftAuditorIds.slice(),
    coAuditorIds: (state.scheduleDraftCoAuditorIds||[]).filter(function(x){ return state.scheduleDraftAuditorIds.indexOf(x)<0; }),
    active: !!state.scheduleDraftActive,
    multiWorkplace: multiWorkplace,
    spreadWorkplaces: !multiWorkplace && !!state.scheduleDraftSpreadWorkplaces,
    skipWeekends: !!state.scheduleDraftSkipWeekends,
    name: (state.scheduleDraftName||'').trim(),
    updatedAt: new Date().toISOString()
  };
  if(id==='new'){
    data.createdAt = new Date().toISOString();
    queueWrite(function(){ return db.collection('schedules').add(data); });
  } else {
    queueWrite(function(){ return db.doc('schedules/'+id).update(data); });
  }
  state.editScheduleId = null;
  render();
};
App.deleteSchedule = function(id){
  if(!canManageContent()) return;
  var s = state.schedules.get(id);
  if(!s) return;
  var cids = s.companyIds;
  if(Array.isArray(cids) && cids.length>0 && !cids.some(isCompanyAccessible)) return;
  queueWrite(function(){ return db.doc('schedules/'+id).delete(); });
};

export {
  isMyOccurrence,
  allVisibleScheduleOccurrences,
  personDisplayName,
  usersWithCompanyAccess,
  renderSchedule,
  renderScheduleManage,
  scheduleListWindow,
  scheduleOccurrenceBadgeHtml,
  sortedAuditorGroups
};
